import { useState, useEffect, useMemo } from "react";
import { Loader2, X, Pencil, Check, Shield, UserPlus, UserMinus, Briefcase, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { LoadingRow, EmptyState } from "./shared/ui";
import { UserLocationsEditor } from "./UserLocationsEditor";

// -------- Users Admin --------
export function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newU, setNewU] = useState({ email: "", name: "", title: "", password: "" });
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [titleDraft, setTitleDraft] = useState("");
  const [currentAdminId, setCurrentAdminId] = useState(null);
  // Round I/L — locaties voor de keuzelijst; leeg laten mag en blijft normaal.
  const [locations, setLocations] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [rich, me, locs] = await Promise.all([
        api.get("/admin/users/rich"),
        api.get("/auth/me"),
        api.get("/locations").catch(() => ({ data: [] })),
      ]);
      setUsers(rich.data);
      setCurrentAdminId(me.data.id);
      setLocations((locs.data || []).map((l) => l.name));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const changeLocations = async (u, next) => {
    setSavingId(u.id);
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, { locations: next });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, location: data.location, locations: data.locations } : x)));
      toast.success(data.locations.length ? `Locaties: ${data.locations.join(", ")}.` : "Locaties leeggemaakt.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  const changeReminder = async (u, on) => {
    setSavingId(u.id);
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, { weekly_reminder: on });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, weekly_reminder: data.weekly_reminder } : x)));
      toast.success(on ? "Herinneringsmails aan." : "Herinneringsmails uit.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const adminCount = useMemo(
    () => users.filter((u) => u.role === "admin").length,
    [users]
  );

  const createUser = async () => {
    const email = newU.email.trim().toLowerCase();
    const name = newU.name.trim();
    const password = newU.password;
    if (!email || !name) {
      toast.error("Naam en e-mail zijn verplicht.");
      return;
    }
    if (!password || password.length < 8) {
      toast.error("Wachtwoord moet minstens 8 tekens zijn.");
      return;
    }
    setCreating(true);
    try {
      await api.post("/auth/register", {
        email,
        name,
        password,
        title: newU.title.trim() || null,
      });
      toast.success("Gebruiker toegevoegd.");
      setNewU({ email: "", name: "", title: "", password: "" });
      setShowNew(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const saveTitle = async (u) => {
    const next = titleDraft.trim();
    if ((u.title || "") === next) {
      setEditingTitleId(null);
      return;
    }
    setSavingId(u.id);
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, { title: next });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, title: data.title } : x)));
      toast.success("Functie bijgewerkt.");
      setEditingTitleId(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  const toggleRole = async (u) => {
    if (u.id === currentAdminId) {
      toast.error("Je kunt je eigen rol niet wijzigen.");
      return;
    }
    const nextRole = u.role === "admin" ? "user" : "admin";
    if (u.role === "admin" && adminCount <= 1) {
      toast.error("De laatste admin kan niet worden gedegradeerd.");
      return;
    }
    if (!window.confirm(
      nextRole === "admin"
        ? `${u.name} als admin instellen?`
        : `${u.name} degraderen naar gewone gebruiker?`
    )) return;
    setSavingId(u.id);
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, { role: nextRole });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, role: data.role } : x)));
      toast.success(nextRole === "admin" ? "Nu admin." : "Nu gewone gebruiker.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  const changeRole = async (u, nextRole) => {
    if (nextRole === u.role) return;
    if (u.id === currentAdminId) {
      toast.error("Je kunt je eigen rol niet wijzigen.");
      return;
    }
    if (u.role === "admin" && adminCount <= 1) {
      toast.error("De laatste admin kan niet worden gewijzigd.");
      return;
    }
    const labels = { user: "gewone gebruiker", admin: "admin", lezer: "lezer (alleen lezen)" };
    if (!window.confirm(`${u.name} instellen als ${labels[nextRole]}?`)) return;
    setSavingId(u.id);
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, { role: nextRole });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, role: data.role } : x)));
      toast.success(`Rol gewijzigd naar ${labels[nextRole]}.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  const deleteUser = async (u) => {
    if (u.id === currentAdminId) {
      toast.error("Je kunt jezelf niet verwijderen.");
      return;
    }
    if (u.role === "admin" && adminCount <= 1) {
      toast.error("De laatste admin kan niet worden verwijderd.");
      return;
    }
    if (!window.confirm(
      `${u.name} definitief verwijderen? Hun ${u.memo_count || 0} memo's blijven staan in de kluis van het team.`
    )) return;
    setSavingId(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success("Gebruiker verwijderd.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div data-testid="admin-gebruikers-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">
          TEAMLEDEN
        </div>
        <div className="flex items-center gap-4">
          <div className="font-mono text-sm text-tw-yellow" data-testid="admin-users-count">
            {users.length}
          </div>
          <Button
            onClick={() => setShowNew((v) => !v)}
            data-testid="admin-users-toggle-new"
            className="h-9 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-[10px] font-bold px-3"
          >
            <UserPlus size={12} className="mr-1" strokeWidth={3} /> Nieuw
          </Button>
        </div>
      </div>

      <p className="mb-4 flex items-start gap-1.5 text-[11px] text-tw-muted leading-relaxed" data-testid="admin-users-role-hint">
        <BookOpen size={12} strokeWidth={2.5} className="text-tw-yellow shrink-0 mt-0.5" />
        <span>Lezer: kan alleen de Bibliotheek lezen — niet inspreken of beheren.</span>
      </p>

      {showNew && (
        <div
          className="bg-tw-surface border border-tw-yellow p-4 mb-4 space-y-3 tw-fadeup"
          data-testid="admin-users-new-form"
        >
          <div className="font-mono text-[10px] tracking-[0.3em] text-tw-yellow">
            NIEUW TEAMLID
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              value={newU.name}
              onChange={(e) => setNewU((p) => ({ ...p, name: e.target.value }))}
              placeholder="Naam (bv. Kees)"
              data-testid="admin-users-new-name"
              className="bg-tw-black border-tw text-white h-10 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-yellow"
            />
            <Input
              value={newU.email}
              onChange={(e) => setNewU((p) => ({ ...p, email: e.target.value }))}
              placeholder="E-mail"
              type="email"
              data-testid="admin-users-new-email"
              className="bg-tw-black border-tw text-white h-10 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-yellow"
            />
            <Input
              value={newU.title}
              onChange={(e) => setNewU((p) => ({ ...p, title: e.target.value }))}
              placeholder="Functie (bv. TD, Sales, Marketing)"
              data-testid="admin-users-new-title"
              className="bg-tw-black border-tw text-white h-10 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-yellow"
            />
            <Input
              value={newU.password}
              onChange={(e) => setNewU((p) => ({ ...p, password: e.target.value }))}
              placeholder="Startwachtwoord (min. 8 tekens)"
              type="text"
              data-testid="admin-users-new-password"
              className="bg-tw-black border-tw text-white h-10 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-yellow"
            />
          </div>
          <p className="text-[11px] text-tw-muted leading-relaxed" data-testid="admin-users-new-role-hint">
            Nieuwe teamleden starten als gebruiker. Zet iemand daarna op <span className="text-tw-yellow">Lezer</span> als die alleen mag meelezen — een lezer kan alleen de Bibliotheek lezen, niet inspreken of beheren.
          </p>
          <div className="flex gap-2 justify-end">
            <Button
              onClick={() => {
                setShowNew(false);
                setNewU({ email: "", name: "", title: "", password: "" });
              }}
              variant="outline"
              className="h-9 rounded-none bg-transparent border-tw text-white hover:bg-tw-surface-hover hover:text-white uppercase tracking-[0.2em] text-[10px]"
            >
              Annuleer
            </Button>
            <Button
              onClick={createUser}
              disabled={creating || !newU.name.trim() || !newU.email.trim() || !newU.password}
              data-testid="admin-users-new-save"
              className="h-9 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-[10px] font-bold disabled:opacity-50"
            >
              {creating ? <Loader2 className="animate-spin" size={14} /> : "Opslaan"}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <LoadingRow />
      ) : users.length === 0 ? (
        <EmptyState label="Nog geen teamleden" hint="Voeg je eerste teamlid toe." testId="admin-users-empty" />
      ) : (
        <div className="space-y-2" data-testid="admin-users-list">
          {users.map((u, i) => {
            const isSelf = u.id === currentAdminId;
            const isEditingTitle = editingTitleId === u.id;
            return (
              <article
                key={u.id}
                data-testid={`admin-user-${u.id}`}
                className="tw-fadeup bg-tw-surface border border-tw p-4 hover:border-tw-yellow transition-colors duration-150"
                style={{ animationDelay: `${Math.min(i * 20, 240)}ms` }}
              >
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-medium">{u.name}</span>
                      {u.role === "admin" && (
                        <span
                          data-testid={`admin-user-role-badge-${u.id}`}
                          className="inline-flex items-center gap-1 border border-tw-yellow text-tw-yellow px-1.5 py-0.5 font-mono text-[9px] tracking-[0.3em] uppercase"
                        >
                          <Shield size={9} strokeWidth={2.8} /> Admin
                        </span>
                      )}
                      {u.role === "lezer" && (
                        <span
                          data-testid={`admin-user-lezer-badge-${u.id}`}
                          className="inline-flex items-center gap-1 border border-tw text-tw-muted px-1.5 py-0.5 font-mono text-[9px] tracking-[0.3em] uppercase"
                        >
                          <BookOpen size={9} strokeWidth={2.8} /> Lezer
                        </span>
                      )}
                      {isSelf && (
                        <span className="font-mono text-[9px] tracking-[0.3em] text-tw-muted uppercase">
                          jij
                        </span>
                      )}
                    </div>
                    <div className="mt-1 font-mono text-[11px] text-tw-muted">{u.email}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <Briefcase size={11} className="text-tw-muted" strokeWidth={2.5} />
                      {isEditingTitle ? (
                        <>
                          <Input
                            value={titleDraft}
                            onChange={(e) => setTitleDraft(e.target.value)}
                            placeholder="Functie"
                            data-testid={`admin-user-title-input-${u.id}`}
                            className="bg-tw-black border-tw text-white h-8 w-48 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-yellow text-xs"
                          />
                          <button
                            type="button"
                            onClick={() => saveTitle(u)}
                            disabled={savingId === u.id}
                            data-testid={`admin-user-title-save-${u.id}`}
                            className="text-tw-yellow hover:text-white transition-colors disabled:opacity-40"
                            aria-label="Functie opslaan"
                          >
                            {savingId === u.id ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Check size={14} strokeWidth={2.8} />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingTitleId(null)}
                            className="text-tw-muted hover:text-white transition-colors"
                            aria-label="Annuleer"
                          >
                            <X size={14} strokeWidth={2.5} />
                          </button>
                        </>
                      ) : (
                        <>
                          <span
                            className="text-xs text-tw-muted"
                            data-testid={`admin-user-title-${u.id}`}
                          >
                            {u.title || <em className="opacity-60">geen functie</em>}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingTitleId(u.id);
                              setTitleDraft(u.title || "");
                            }}
                            data-testid={`admin-user-title-edit-${u.id}`}
                            className="text-tw-muted hover:text-tw-yellow transition-colors"
                            aria-label="Wijzig functie"
                            title="Wijzig functie"
                          >
                            <Pencil size={12} strokeWidth={2.5} />
                          </button>
                        </>
                      )}
                    </div>
                    <UserLocationsEditor user={u} locations={locations} disabled={savingId === u.id}
                      onSave={(next) => changeLocations(u, next)} />
                    {u.role !== "lezer" && (
                      <label className="mt-3 flex items-center gap-2 text-xs text-tw-muted cursor-pointer w-fit" data-testid={`admin-user-reminder-${u.id}`}>
                        <Switch checked={u.weekly_reminder !== false} disabled={savingId === u.id}
                          onCheckedChange={(on) => changeReminder(u, on)} data-testid={`admin-user-reminder-switch-${u.id}`}
                          className="data-[state=checked]:bg-tw-yellow" />
                        <span>Herinneringsmails</span>
                        <span className="opacity-70">· maandag: open locatievragen en, na 14 dagen stilte, één vraag voor eigen werk</span>
                      </label>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className="font-mono text-[10px] text-tw-muted uppercase tracking-widest">
                      {u.memo_count || 0} memo{(u.memo_count || 0) === 1 ? "" : "'s"}
                    </span>
                    <div className="flex items-center gap-1">
                      <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={savingId === u.id || isSelf}>
                        <SelectTrigger
                          data-testid={`admin-user-role-select-${u.id}`}
                          className="h-8 w-[116px] rounded-none bg-tw-black border-tw text-white text-xs focus:ring-0 focus:border-tw-yellow disabled:opacity-40"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-tw-surface border-tw text-white rounded-none">
                          <SelectItem value="user" title="Kan opnemen, verwerken en de eigen kluis beheren." className="rounded-none text-xs">Gebruiker</SelectItem>
                          <SelectItem value="admin" title="Volledige toegang — alle memo's, gebruikers en instellingen beheren." className="rounded-none text-xs">Beheerder</SelectItem>
                          <SelectItem value="lezer" data-testid={`admin-user-role-lezer-${u.id}`} title="Kan alleen de Bibliotheek lezen — niet inspreken of beheren." className="rounded-none text-xs">Lezer</SelectItem>
                        </SelectContent>
                      </Select>
                      <button
                        type="button"
                        onClick={() => deleteUser(u)}
                        disabled={savingId === u.id || isSelf}
                        data-testid={`admin-user-delete-${u.id}`}
                        title="Verwijder"
                        aria-label="Verwijder"
                        className="p-2 text-tw-muted hover:text-tw-red transition-colors disabled:opacity-30 disabled:hover:text-tw-muted"
                      >
                        {savingId === u.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <UserMinus size={14} strokeWidth={2.5} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
